import React, { useCallback, useContext, useState } from 'react'
import styled from 'styled-components'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import cx from 'clsx'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { ButtonToggleItem } from 'components/Common/ButtonGroupToggle/ButtonGroupToggleItem'
import ButtonGroupToggle from 'components/Common/ButtonGroupToggle'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CruiseWhenSelectMonths from './CruiseWhenSelectContentMonths'
import CruiseDateSelectCalendar from './CruiseWhenSelectCalendar'
import CruiseWhenSelectHowLong from './CruiseWhenSelectHowLong'

const CruiseWhenListWrapper = styled(Group)`
  padding: ${rem(8)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} 0;
    max-height: 70vh;
    overflow-y: auto;
  }

  &.drawer {
    max-height: unset;
  }
`

const TabGroupToggle = styled(ButtonGroupToggle)`
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(340)};
  }

  &.drawer {
    width: ${rem(300)};
  }
`

const AnytimeContent = styled.div`
  padding: ${rem(16)};
`

const TabButtons: Array<ButtonToggleItem> = [
  { id: 'departure-month', label: 'Months' },
  { id: 'date-range', label: 'Date Range' },
  { id: 'anytime', label: 'Anytime' },
]

interface Props {
  onSelect?: () => void;
  onChange?: (value: App.CruiseGlobalFilters) => void;
  onClose?: () => void;
  drawerMode?: boolean;
}

function CruiseWhenSelectContent(props: Props) {
  const { drawerMode, onSelect = noop, onChange = noop } = props
  const { checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)

  const defaultTab = checkinDate && checkoutDate ? TabButtons[1] : TabButtons[0]
  const [tab, setTab] = useState<ButtonToggleItem>(defaultTab)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const onTabSelect = useCallback((nextTab: ButtonToggleItem) => {
    if (nextTab.id === 'anytime') {
      searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE, flexibleMonths: '' })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: undefined })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: undefined })
    }

    setTab(nextTab)
  }, [searchDispatch])

  return <CruiseWhenListWrapper
    direction="vertical"
    gap={20}
    className={cx({ drawer: drawerMode })}
  >
    <Group direction="vertical" gap={32}>
      <CruiseWhenSelectHowLong onSelect={onSelect} drawerMode={drawerMode} />

      <Group direction="vertical" gap={16}>
        <Heading align="center" variant="heading6">When would you like to sail?</Heading>

        <Group direction="horizontal" horizontalAlign="center">
          <TabGroupToggle
            items={TabButtons}
            activeItem={tab}
            onItemClick={onTabSelect}
            className={cx({ drawer: drawerMode })}
          />
        </Group>

        {tab.id === 'departure-month' && <CruiseWhenSelectMonths
          onSelect={onSelect}
          onChange={onChange}
          drawerMode={drawerMode}
        />}

        {tab.id === 'date-range' && <CruiseDateSelectCalendar type={drawerMode ? 'single' : 'dual'} />}

        {tab.id === 'anytime' && <AnytimeContent data-testid="cruise-when-select-anytime">
          <BodyText variant="medium" align="center">Find all available cruises.</BodyText>
        </AnytimeContent>}
      </Group>
    </Group>
  </CruiseWhenListWrapper>
}

export default CruiseWhenSelectContent
